/**
 * Workshop Constants & Configuration
 * Centralized configuration for the workshops feature
 */

// ========================================
// SORT OPTIONS
// ========================================

export const SORT_OPTIONS = [
  { value: "startDate", label: "Starting Soon" },
  { value: "participants", label: "Most Popular" },
  { value: "created", label: "Newest" },
  { value: "price", label: "Price" },
];

export const SORT_ORDERS = [
  { value: "asc", label: "Ascending", icon: "↑" },
  { value: "desc", label: "Descending", icon: "↓" },
];

// ========================================
// FILTER OPTIONS
// ========================================

export const FORMAT_OPTIONS = [
  { value: "all", label: "All Formats" },
  { value: "online", label: "Online" },
  { value: "physical", label: "Physical" },
];

export const PRICING_OPTIONS = [
  { value: "all", label: "All" },
  { value: "free", label: "Free Only" },
  { value: "paid", label: "Paid Only" },
];

export const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "upcoming", label: "Upcoming" },
  { value: "ongoing", label: "Ongoing" },
  { value: "completed", label: "Completed" },
];

// ========================================
// QUICK FILTERS (for Hero Section)
// ========================================

export const QUICK_FILTERS = [
  { key: "pricing", value: "free", label: "Free", icon: "💰" },
  { key: "pricing", value: "paid", label: "Paid", icon: "💳" },
  { key: "format", value: "online", label: "Online", icon: "🌐" },
  { key: "format", value: "physical", label: "Physical", icon: "📍" },
  { key: "status", value: "upcoming", label: "Starting Soon", icon: "⏰" },
];

// ========================================
// POPULAR TAGS
// ========================================

export const POPULAR_TAGS = [
  "Machine Learning",
  "Data Science",
  "Python",
  "Statistics",
  "Deep Learning",
  "NLP",
  "Computer Vision",
  "Research Methods",
  "Bioinformatics",
  "Quantum Computing",
  "R Programming",
  "MATLAB",
  "Data Visualization",
  "Clinical Research",
  "Academic Writing",
];

// ========================================
// PAGINATION
// ========================================

export const PAGINATION_CONFIG = {
  defaultPage: 1,
  defaultLimit: 20,
  maxLimit: 50,
  minLimit: 10,
  maxVisiblePages: 5, // Maximum page numbers to show in pagination
};

// ========================================
// PRICE RANGES
// ========================================

export const PRICE_RANGES = [
  { min: 0, max: 0, label: "Free" },
  { min: 0, max: 1000, label: "Under Rs. 1,000" },
  { min: 1000, max: 5000, label: "Rs. 1,000 - 5,000" },
  { min: 5000, max: 10000, label: "Rs. 5,000 - 10,000" },
  { min: 10000, max: null, label: "Over Rs. 10,000" },
];

// ========================================
// CATEGORY ICONS
// ========================================

export const CATEGORY_ICONS = {
  "Machine Learning": "🤖",
  "Data Science": "📊",
  Statistics: "📈",
  Biology: "🧬",
  Physics: "⚛️",
  Chemistry: "🧪",
  "Computer Science": "💻",
  Mathematics: "🔢",
  Programming: "👨‍💻",
  "Research Methods": "🔬",
  Medicine: "⚕️",
  Engineering: "⚙️",
  "Social Sciences": "👥",
  Economics: "💰",
  Psychology: "🧠",
  Bioinformatics: "🧬",
  "Web Development": "🌐",
  "Mobile Development": "📱",
  "Artificial Intelligence": "🤖",
  Blockchain: "⛓️",
  Cybersecurity: "🔐",
  "UI/UX Design": "🎨",
  "Digital Marketing": "📱",
  "Project Management": "📋",
};

// ========================================
// STATUS BADGES
// ========================================

export const STATUS_BADGES = {
  approved: {
    text: "✓ Enrolled",
    className: "status-enrolled",
    color: "#10b981",
  },
  pending: {
    text: "Pending Approval",
    className: "status-pending",
    color: "#f59e0b",
  },
  payment_pending: {
    text: "Payment Pending",
    className: "status-payment",
    color: "#3b82f6",
  },
  rejected: {
    text: "Rejected",
    className: "status-rejected",
    color: "#ef4444",
  },
};

// ========================================
// WORKSHOP STATUS
// ========================================

export const WORKSHOP_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  UPCOMING: "upcoming",
  ONGOING: "ongoing",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
};

// ========================================
// BREAKPOINTS (for responsive design)
// ========================================

export const BREAKPOINTS = {
  mobile: 768,
  tablet: 1024,
  desktop: 1280,
  wide: 1536,
};

// ========================================
// DEBOUNCE DELAYS (milliseconds)
// ========================================

export const DEBOUNCE_DELAYS = {
  search: 300, // Search input
  filter: 200, // Filter changes
  resize: 150, // Window resize
};

// ========================================
// CACHE DURATIONS (milliseconds)
// ========================================

export const CACHE_DURATIONS = {
  workshops: 5 * 60 * 1000, // 5 minutes
  featured: 10 * 60 * 1000, // 10 minutes
  categories: 30 * 60 * 1000, // 30 minutes
  stats: 15 * 60 * 1000, // 15 minutes
};

// ========================================
// ERROR MESSAGES
// ========================================

export const ERROR_MESSAGES = {
  network: "Network error. Please check your connection.",
  server: "Server error. Please try again later.",
  notFound: "Workshop not found.",
  unauthorized: "Please login to continue.",
  forbidden: "You don't have permission to perform this action.",
  validation: "Please check your input and try again.",
  generic: "Something went wrong. Please try again.",
};

// ========================================
// SUCCESS MESSAGES
// ========================================

export const SUCCESS_MESSAGES = {
  applied: "Application submitted successfully!",
  enrolled: "You are enrolled! Check your email for details.",
  paymentPending: "Application submitted. Please upload payment proof.",
};

// ========================================
// FEATURED CAROUSEL CONFIG
// ========================================

export const CAROUSEL_CONFIG = {
  autoPlayInterval: 5000, // 5 seconds
  transitionDuration: 300, // 300ms
  itemsPerView: {
    mobile: 1,
    tablet: 2,
    desktop: 3,
  },
};

// ========================================
// STATS BAR CONFIG
// ========================================

export const STATS_BAR_ITEMS = [
  { icon: "🎓", label: "Workshops", key: "totalWorkshops" },
  { icon: "👥", label: "Learners", key: "totalParticipants" },
  { icon: "📚", label: "Ongoing", key: "ongoingWorkshops" },
  { icon: "✅", label: "Completed", key: "completedWorkshops" },
];

// ========================================
// DEFAULT FILTER STATE
// ========================================

export const DEFAULT_FILTERS = {
  category: "all",
  field: "all",
  format: "all",
  pricing: "all",
  status: "upcoming", // Default to upcoming workshops
  minPrice: null,
  maxPrice: null,
  sortBy: "startDate",
  sortOrder: "asc",
};
